import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ValuationService } from '../../../services/valuation.service';

const APN_REGEX = /^[A-Za-z0-9]{1,5}( ?-[A-Za-z0-9]{1,5}){1,4}$/;

@Component({
  selector: 'app-new-valuation-page',
  imports: [],
  templateUrl: './new-valuation-page.component.html',
  styleUrl: './new-valuation-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class NewValuationPageComponent {
  private readonly valuationService = inject(ValuationService);
  private readonly router = inject(Router);

  readonly apn = signal('');
  readonly appealYear = signal<number | null>(null);
  readonly appealYearDisplay = computed(() => this.appealYear()?.toString() ?? '');
  readonly loading = signal(false);
  readonly apiError = signal<string | null>(null);
  readonly conflictSessionId = signal<string | null>(null);

  isApnValid(): boolean {
    return APN_REGEX.test(this.apn());
  }

  isYearValid(): boolean {
    const y = this.appealYear();
    return y !== null && y >= 2000 && y <= 2100;
  }

  isFormValid(): boolean {
    return this.isApnValid() && this.isYearValid();
  }

  setApn(value: string): void {
    this.apn.set(value);
    this.apiError.set(null);
    this.conflictSessionId.set(null);
  }

  setAppealYear(value: string): void {
    const parsed = parseInt(value, 10);
    this.appealYear.set(isNaN(parsed) ? null : parsed);
    this.apiError.set(null);
    this.conflictSessionId.set(null);
  }

  async submit(): Promise<void> {
    if (!this.isFormValid() || this.loading()) return;
    this.loading.set(true);
    this.apiError.set(null);
    this.conflictSessionId.set(null);
    try {
      const response = await firstValueFrom(
        this.valuationService.createValuation(this.apn(), this.appealYear()!),
      );
      void this.router.navigate(['/valuations', response.id]);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.loading.set(false);
    }
  }

  navigateToExisting(): void {
    const id = this.conflictSessionId();
    if (id) void this.router.navigate(['/valuations', id]);
  }

  private handleError(error: unknown): void {
    if (!(error instanceof HttpErrorResponse)) {
      this.apiError.set('An error occurred. Please try again.');
      return;
    }
    const body = error.error as Record<string, unknown>;
    // FastAPI HTTPException wraps payload in { detail: ... }; JSONResponse uses the root.
    const detail = (body?.['detail'] ?? body) as Record<string, unknown>;
    if (error.status === 404 && detail?.['error'] === 'no_apn_match') {
      this.apiError.set(`No property found for APN ${this.apn()}.`);
    } else if (error.status === 400 && detail?.['error'] === 'unsupported_property_type') {
      this.apiError.set(`Property type "${detail['property_type']}" is not supported.`);
    } else if (error.status === 409 && detail?.['error'] === 'session_exists') {
      this.conflictSessionId.set(detail['id'] as string);
    } else {
      this.apiError.set('An error occurred. Please try again.');
    }
  }
}
